import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { api } from "../lib/api";
import { UI, LANG } from "../data/content";
import { Reveal, SectionHead, ContactModule, usePageMeta } from "../components/kit";

const F = () => UI.funding;
const name = (p) => (LANG === "el" ? p.name_el : p.name_en);
const desc = (p) => (LANG === "el" ? p.description_el : p.description_en);
const fmtD = (d) => (d ? new Date(d).toLocaleDateString(LANG === "el" ? "el-GR" : "en-GB", { day: "numeric", month: "long", year: "numeric" }) : "—");

const VerifyChip = ({ rec }) => (
  <div className="flex flex-wrap items-center gap-3">
    <span className="topic-tag" data-testid="verified-chip">{F().verified}: {fmtD(rec.verified_at)}</span>
    {rec.source_url && <a href={rec.source_url} target="_blank" rel="noreferrer" className="text-brand text-sm underline" data-testid="source-link">{F().source}</a>}
    {rec.stale && <span className="text-xs font-semibold" style={{ color: "#b03535" }} data-testid="stale-warning">{F().staleWarn}</span>}
  </div>
);

export const FundingHub = () => {
  const [programmes, setProgrammes] = useState([]);
  usePageMeta(`${F().title} | KairosHK`, F().lead);
  useEffect(() => { api.get("/funding/programmes").then(({ data }) => setProgrammes(data)).catch(() => {}); }, []);
  const groups = ["GR", "CY", "EU"].map((c) => ({ c, items: programmes.filter((p) => p.country === c) })).filter((g) => g.items.length);
  return (
    <div data-testid="funding-hub-page">
      <section className="k-section !pb-10">
        <div className="k-container">
          <p className="eyebrow mb-4">{F().eyebrow}</p>
          <h1 className="k-h1 mb-5">{F().title}</h1>
          <p className="standfirst max-w-3xl mb-8">{F().lead}</p>
          <Link to="/funding/explorer" className="btn-primary" data-testid="open-explorer-btn">{F().explorer}</Link>
        </div>
      </section>
      {groups.map(({ c, items }) => (
        <section key={c} className="k-section !py-12 odd:bg-paper-alt">
          <div className="k-container">
            <SectionHead eyebrow={F().countries[c]} title={F().countries[c]} />
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {items.map((p) => (
                <Link key={p.slug} to={`/funding/${p.country.toLowerCase()}/${p.slug}`} className="cap-card" data-testid={`programme-card-${p.slug}`}>
                  <h3 className="cap-title text-lg font-semibold mb-1">{name(p)}</h3>
                  {LANG !== "el" && p.name_el !== p.name_en && <p className="text-sm text-ink-muted mb-2">{p.name_el}</p>}
                  <p className="text-sm text-ink-muted leading-relaxed flex-1">{desc(p)}</p>
                  <p className="text-xs text-ink-muted mt-4">{F().verified}: {fmtD(p.verified_at)}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      ))}
    </div>
  );
};

export const ProgrammePage = () => {
  const { country, slug } = useParams();
  const [p, setP] = useState(undefined);
  useEffect(() => {
    api.get(`/funding/programmes/${slug}`).then(({ data }) => setP(data)).catch(() => setP(null));
  }, [slug]);
  usePageMeta(p ? `${name(p)} | KairosHK` : "KairosHK", p ? desc(p) : "");
  if (p === undefined) return <div className="k-section k-container text-ink-muted">…</div>;
  if (p === null || p.country.toLowerCase() !== country) return <Navigate to="/funding" replace />;
  const facts = [
    [F().authority, LANG === "el" ? p.authority_el : p.authority_en],
    [F().legalBasis, p.legal_basis],
    [F().fundingType, p.funding_type],
    [F().period, p.period],
    [F().scope, p.scope],
    [F().intensity, p.typical_intensity_min != null ? `${p.typical_intensity_min}–${p.typical_intensity_max}%` : "—"],
  ];
  return (
    <div data-testid={`programme-page-${p.slug}`}>
      <section className="k-section !pb-8">
        <div className="k-container">
          <Link to="/funding" className="arrow-link mb-6 inline-flex" data-testid="back-to-funding">← {F().back}</Link>
          <p className="eyebrow mb-4">{F().countries[p.country]}</p>
          <h1 className="k-h1 mb-3">{name(p)}</h1>
          {LANG !== "el" && p.name_el !== p.name_en && <p className="standfirst mb-5">{p.name_el}</p>}
          <p className="text-lg leading-relaxed max-w-3xl mb-6">{desc(p)}</p>
          <VerifyChip rec={p} />
        </div>
      </section>
      <section className="k-section !py-12 bg-paper-alt">
        <div className="k-container">
          <Reveal>
            <table className="k-table max-w-3xl" data-testid="programme-facts-table">
              <tbody>
                {facts.map(([k, v]) => (
                  <tr key={k}><td className="font-semibold w-56">{k}</td><td className="text-ink-muted">{v || "—"}</td></tr>
                ))}
              </tbody>
            </table>
            {p.indicative && <p className="text-xs text-ink-muted mt-4 max-w-3xl" data-testid="indicative-note">{F().indicative}</p>}
          </Reveal>
        </div>
      </section>
      <section className="k-section !py-12">
        <div className="k-container">
          <SectionHead eyebrow={F().eyebrow} title={F().calls} />
          {p.calls.length === 0 ? (
            <p className="text-ink-muted" data-testid="no-calls-note">{F().noCalls}</p>
          ) : (
            <div className="border-t border-line max-w-4xl" data-testid="programme-calls">
              {p.calls.map((c) => (
                <div key={c.fcall_id} className="py-5 border-b border-line">
                  <p className="font-medium">{LANG === "el" ? c.title_el : c.title_en}</p>
                  <p className="text-xs text-ink-muted mt-1">{c.status} · {c.closes_at ? `${F().closes} ${fmtD(c.closes_at)}` : ""} · {F().verified}: {fmtD(c.verified_at)}</p>
                </div>
              ))}
            </div>
          )}
          <p className="text-sm leading-relaxed border border-line bg-paper-alt p-6 mt-10 max-w-3xl" data-testid="qualified-handoff">{F().handoff}</p>
        </div>
      </section>
      <section className="k-container pb-20"><ContactModule /></section>
    </div>
  );
};

export const FundingExplorer = null;

export default FundingHub;
